import { access, copyFile, readdir, readFile, writeFile } from 'node:fs/promises';
import { execFile as execFileCb } from 'node:child_process';
import { promisify } from 'node:util';
import { createHash } from 'node:crypto';
import { basename, resolve } from 'node:path';

import type { BuildSeaOptions } from '../../types/build.d.ts';

const execFile = promisify(execFileCb);

/**
 * Escapes a string for safe interpolation inside a single-quoted JS string literal.
 */
function escapeForSingleQuotedString(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

export async function buildSea(name: string, options?: BuildSeaOptions): Promise<string> {
    const outDir = options?.outDir ?? './build';
    const nodeExecutable = options?.config?.executable ?? process.execPath;

    const safeName = escapeForSingleQuotedString(name);
    const safeVersion = escapeForSingleQuotedString(options?.version ?? '');
    const safeOutDir = escapeForSingleQuotedString(outDir.replace(/\\/g, '/'));

    // Use the target node executable to create the output directory and resolve
    // platform-specific paths. This also validates the executable is functional.
    const { stdout } = await execFile(nodeExecutable, ['-e', `
        const { mkdirSync } = require('node:fs');
        const path = require('node:path');
        const appVersion = '${safeVersion}'.trim();
        const dirName = '${safeName}' + (appVersion ? '-v' + appVersion : '') + '.' + process.platform + '-' + process.arch;
        const dir = path.resolve('${safeOutDir}', dirName);
        mkdirSync(dir, { recursive: true });
        process.stdout.write(JSON.stringify({ dirName, nodeVersion: process.version, platform: process.platform, arch: process.arch }));
    `.trim()]);

    const { dirName, nodeVersion, platform, arch } = JSON.parse(stdout);

    // Step 1: Produce the SEA JavaScript file
    let seaMain: string;
    if (options?.esbuild) {
        let esbuild: typeof import('esbuild');
        try {
            esbuild = await import('esbuild');
        }
        catch {
            throw new Error('esbuild is required to bundle the entry point. Install it with:\n\n  npm install --save-dev esbuild\n');
        }
        seaMain = `${outDir}/${name}.js`;
        await esbuild.build({
            format: 'cjs',
            target: 'node20',
            platform: 'node',
            bundle: true,
            external: ['esbuild', 'postject'], // exclude esbuild from the bundle since it's only needed at build time
            loader: {
                ".md": "file",
                ".d.ts": "file",
            },
            assetNames: `${dirName}/[name]`,
            entryNames: '[name]',
            outdir: outDir,
            minify: true,
            logLevel: 'info',
            ...options.esbuild,
        });
    }
    else if (options?.config?.main) {
        seaMain = resolve(options.config.main);
    }
    else {
        throw new Error('Either esbuild options (with entryPoints) or config.main must be provided.');
    }

    // Write SEA config file in the build folder
    const blobPath = resolve(`${outDir}/${name}.sea.node-${nodeVersion}-${platform}-${arch}.blob`);
    const configPath = resolve(`${outDir}/${name}.sea-config.${dirName}.json`);
    const seaConfig = {
        disableExperimentalSEAWarning: true,
        useSnapshot: false,
        useCodeCache: false,
        ...options?.config,
        main: resolve(seaMain),
        output: blobPath,
    };
    await writeFile(configPath, JSON.stringify(seaConfig, null, 2));

    const nativePath = resolve(outDir, dirName);

    const isWindows = platform === 'win32';
    const isMacos = platform === 'darwin';
    const targetFile = isWindows ? `${name}.exe` : name;
    const targetPath = `${nativePath}/${targetFile}`;


    // Check if we can skip the SEA build
    const seaJsContent = await readFile(seaMain);
    const shaPath = `${outDir}/${basename(seaMain)}.sha`;

    const currentSha = createHash('sha256').update(seaJsContent).digest('hex');
    let canSkip = false;

    try {
        const [previousSha] = await Promise.all([
            readFile(shaPath, 'utf8'),
            access(targetPath)
        ]);
        canSkip = previousSha === currentSha;
    }
    catch {
        // SHA file or target doesn't exist, need to build
    }
    if (canSkip) {
        console.log('SEA build skipped - esbuild output unchanged and target exists');
        return targetPath;
    }

    // Step 3: Generate the blob to be injected
    await execFile(nodeExecutable, ['--experimental-sea-config', configPath]);

    // Step 4: Copy the Node.js executable to the target path
    await copyFile(nodeExecutable, targetPath);

    // Step 5: Remove the signature of the binary
    if (isWindows) {
        try {
            const signtool = await findSigntool();
            if (signtool) {
                console.log(`Found signtool at: ${signtool}`);
                await execFile(signtool, ['remove', '/s', targetPath]);
                console.log(`Signature removed from ${targetPath} executable`);
            }
        }
        catch (e: any) {
            console.warn('Failed to remove signature:', e.message);
        }
    }
    else if (isMacos) {
        try {
            await execFile('codesign', ['--remove-signature', targetPath]);
            console.log(`Signature removed from ${targetPath} executable`);
        }
        catch (e: any) {
            console.warn('Failed to remove signature:', e.message);
        }
    }

    // Step 6: Inject the SEA blob into the binary
    type InjectOptions = {
        /**
         * @default '__POSTJECT'
         */
        machoSegmentName?: string;
        /**
         * @default false
         */
        overwrite?: boolean;
        /**
         * @default 'POSTJECT_SENTINEL_fce680ab2cc467b6e072b8b5df1996b2'
         */
        sentinelFuse?: string
    };
    type Inject = (filename: string, resourceName: string, resourceData: Buffer, options?: InjectOptions) => Promise<void>;

    let inject: Inject;
    try {
        ({ inject } = await import('postject') as { inject: Inject });
    }
    catch {
        throw new Error('postject is required to build a SEA. Install it with:\n\n  npm install --save-dev postject\n');
    }

    const resourceData = await readFile(blobPath);

    // Construct the sentinel fuse at runtime to avoid the literal appearing in the
    // bundled SEA output — postject would otherwise find multiple occurrences.
    const sentinelFuse = ['NODE', 'SEA', 'FUSE', 'fce680ab2cc467b6e072b8b5df1996b2'].join('_');

    await inject(
        targetPath,
        'NODE_SEA_BLOB',
        resourceData,
        {
            overwrite: true,
            sentinelFuse,
            ...(isMacos ? { machoSegmentName: 'NODE_SEA' } : {})
        });

    // Step 7: Sign the binary
    if (isWindows) {
        try {
            const signtool = await findSigntool();
            if (signtool) {
                console.log(`Signing executable with signtool at: ${signtool}`);
                await execFile(signtool, ['sign', '/fd', 'SHA256', targetPath]);
                console.log(`Executable signed successfully`);
            }
        }
        catch (e: any) {
            console.warn('Failed to sign executable:', e.message);
        }
    }
    else if (isMacos) {
        try {
            await execFile('codesign', ['--sign', '-', targetPath]);
            console.log(`Executable signed successfully`);
        }
        catch (e: any) {
            console.warn('Failed to sign executable:', e.message);
        }
    }

    // Save SHA for next build comparison
    await writeFile(shaPath, currentSha);
    return targetPath;
}

async function findSigntool(): Promise<string | null> {
    const windowsKitsPath = 'C:\\Program Files (x86)\\Windows Kits\\10\\bin';

    try {
        const entries = await readdir(windowsKitsPath, { withFileTypes: true });

        // Filter directories that match version pattern (e.g., 10.0.22621.0)
        const versionDirs = entries
            .filter(entry => entry.isDirectory() && /^10\.0\.\d+\.\d+$/.test(entry.name))
            .map(entry => entry.name)
            .sort((a, b) => {
                // Sort by version number descending to get the latest
                const partsA = a.split('.').map(Number);
                const partsB = b.split('.').map(Number);
                for (let i = 0; i < partsA.length; i++) {
                    if (partsA[i] !== partsB[i]) {
                        return partsB[i] - partsA[i];
                    }
                }
                return 0;
            });

        // Try each version directory, starting with the latest
        for (const version of versionDirs) {
            const signtoolPath = `${windowsKitsPath}\\${version}\\${process.arch}\\signtool.exe`;
            try {
                await access(signtoolPath);
                return signtoolPath;
            }
            catch {
                // signtool not found in this version, try next
            }
        }

        console.warn('signtool.exe not found in any Windows SDK version');
        return null;
    }
    catch (e: any) {
        console.warn(`Windows SDK not found at ${windowsKitsPath}: ${e.message}`);
        return null;
    }
}

